import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, pushesAPose } from '../skillUsage';

export const exaggeratedPosesSkill: CodeoSkill = {
    name: SKILL_NAMES.exaggeratedPoses,
    kind: 'codeo',
    description:
        'Push an action’s poses far past realistic — a crouch to 3.5× wide and a quarter tall, a ' +
        'stretch to 2.5× tall — with exactly the same timing, so the moment reads with energy and ' +
        'emphasis instead of looking stiff.',
    checkLooksFor:
        'a pose pushed well past life: a scaleX or scaleY of 2.2 or more, or of 0.35 or less, or a width and height parted by 4× (2 × 0.5)',
    documentUsesSkill: check((script) => pushesAPose(script)),
    reviewNote:
        'A shape squashed far wider than it is tall, or stretched to more than twice its height, ' +
        'during a jump or a landing is this technique working, not a distorted element.',
    instructions: `# Exaggeration

Two bodies make the same jump. Every phase starts and ends on the same frame, and they rise and fall identically. One is believable and looks stiff; the other is exaggerated and reads with energy, clarity and intent. **The only difference is how far the poses are pushed.** Exaggeration is not a different animation or a different timing. It is the same action with its extremes amplified until they read at a glance.

## The numbers

A body three times as tall as it is wide jumps about 3.5 of its own heights:

| Pose | Restrained | Exaggerated |
|---|---|---|
| crouch and landing squash | 1.5 wide × 0.5 tall | **3.5 wide × 0.25 tall** |
| stretch at the top of the jump | 1 × 1.25 | **1 × 2.5** |

- **Volume is deliberately broken.** At the top the exaggerated body is two and a half times its height at its own width. Pushing past what is physical is the point.
- **The timing is identical**: four phases of a third of a second each. Crouch, launch, fall, recover.
- **Crouch**: a fast drop into the squash that eases into a hold (the anticipation: see \`${SKILL_NAMES.anticipationWindUp}\`).
- **Launch**: explosive. The body leaves the ground and stretches on the same very steep ease-out, so it is fully tall within a couple of frames and decelerates into the apex.
- **Fall**: the launch played backwards, slow at the apex and fastest at impact. The shape rides the same curve back toward the squash, so it is still tall for most of the fall and flattens in the last frames before it lands.
- **Recover**: the exaggerated landing *holds* the squash for an instant before springing back to rest (width and height on slightly different curves). The restrained one simply recovers.

## Markup

\`\`\`html
<div id="ex-stage" style="position: absolute; inset: 0; overflow: hidden;">
  <div id="ex-body" data-l5-absolute style="position: absolute; left: calc(50% - 23px); bottom: 180px; width: 46px; height: 140px; border-radius: 6px; background: var(--l5-color-0, #101820); transform-origin: 50% 100%;"></div>
</div>
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });

CustomEase.create('crouch', 'M0,0 C0.366,0.616 0.292,0.99 1,1');
CustomEase.create('takeOff', 'M0,0 C0,0.135 0.072,1 1,1');
CustomEase.create('drop', 'M0,0 C0.928,0 1,0.865 1,1');
CustomEase.create('recoverX', 'M0,0 C0.494,0.066 0.015,0.995 1,1');
CustomEase.create('recoverY', 'M0,0 C0.465,0.116 0.024,0.989 1,1');

// The poses. Exaggeration lives here and only here; the timing below is the same either way.
const SQUASH = { scaleX: 3.5, scaleY: 0.25 };   // restrained: 1.5 x 0.5
const APEX = { scaleX: 1, scaleY: 2.5 };        // restrained: 1 x 1.25
const HEIGHT = 140, JUMP = 3.5 * HEIGHT;
const PHASE = 1 / 3;
const CROUCH = 0.6;

tl.to('#ex-body', { ...SQUASH, duration: PHASE, ease: 'crouch' }, CROUCH);
const AIR = CROUCH + PHASE;
tl.to('#ex-body', { y: -JUMP, duration: PHASE, ease: 'takeOff' }, AIR);
tl.to('#ex-body', { ...APEX, duration: PHASE, ease: 'takeOff' }, AIR);
const LAND = AIR + 2 * PHASE;
tl.to('#ex-body', { y: 0, duration: PHASE, ease: 'drop' }, LAND - PHASE);
tl.to('#ex-body', { ...SQUASH, duration: PHASE, ease: 'drop' }, LAND - PHASE);
tl.to('#ex-body', { scaleX: 1, duration: PHASE, ease: 'recoverX' }, LAND);
tl.to('#ex-body', { scaleY: 1, duration: PHASE, ease: 'recoverY' }, LAND);

tl.set({}, {}, LAND + PHASE + 0.5);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Exaggerate one moment, not all of them.** The payoff, the verdict, the one beat that has to land: push its poses about 2–3× further than the others. If every beat is exaggerated, nothing is.
- **Any amplitude can be pushed**, at the same timing: the squash of a landing (\`${SKILL_NAMES.squashAndStretch}\`), the distance of a wind-up (\`${SKILL_NAMES.anticipationWindUp}\`), the swing of a loose part (\`${SKILL_NAMES.followThroughOverlap}\`), how far an element overshoots.
- **A landing alone.** For an element that drops in rather than jumps, use the fall, landing squash and recovery: it arrives tall and fast, flattens to 3.5 × 0.25, holds, and springs up to rest.
- **Type.** A word can take this squash and stretch only while it is moving and unreadable anyway. It must be exactly 1 × 1 whenever it is meant to be read. On type, 2 × 0.5 is already a lot.
- **Frame room.** The stretched apex is tall: 3.5 heights of jump plus 2.5 heights of body. Keep the body under about a seventh of the frame's height, or lower the jump to fit.

## Pitfalls

- **Changing the timing as well is a different animation.** Exaggeration is in the amplitude; leave the phases where they are. They are a third of a second in any scene: the whole jump is over in about 1.3s and the body then rests. Slowed to fill a longer scene, the pushed poses play in slow motion and the energy, which is the whole point, is gone.
- **Pivot on the contact edge.** \`transform-origin: 50% 100%\` keeps the base on the ground through the crouch and the landing squash. About the centre, the squashed body floats above the floor.
- Exaggerating something rigid (a phone, a building) breaks it rather than energising it. Push the poses of soft and characterful things; for rigid ones, push the timing contrast instead (\`${SKILL_NAMES.massAndWeight}\`).
- The body is transform-tweened, so it must not carry a CSS \`transform\`; its resting place is in layout.`,
};
